import { type SyntheticEvent, useCallback, useEffect, useRef, useState } from "react";
import { Icon, Input, Label } from "semantic-ui-react";
import cn from "classnames";
import { FixedSizeList as LazyList } from "react-window";

import { TextDotDotDot } from "components/textTruncators/TextTruncators";
import globalEvents from "utils/globalEvents";
import type { FiltersMap } from "utils/filterUtils";
import { useEndReached } from "./hooks/useEndReached";

import "./multiSelectWithWindow.scss";

const getCheckedItems = <T extends MultiSelectWithWindowItem>(
  allItems: T[],
  rawSelected?: (T | number | string | T[])[],
): ItemsMap<T> => {
  const result: ItemsMap<T> = {};
  if (!rawSelected || rawSelected.length === 0) return result;
  const flat: (T | number | string)[] = ([] as (T | number | string)[]).concat(rawSelected as any);
  flat.forEach((entry) => {
    let found: T | undefined;
    if (typeof entry === "number" || typeof entry === "string") {
      found = allItems.find((i) => i.value === entry);
    } else {
      found = allItems.find((i) => i.value === entry.value) || entry; // preserve if not in current page
    }
    if (found) result[found.value] = found;
  });
  return result;
};

export type MultiSelectWithWindowItem = { text: string; key?: string; value: number | string };
type ItemsMap<T extends MultiSelectWithWindowItem> = { [id: string | number]: T };

const ITEM_SIZE = 40;
const MAX_LIST_HEIGHT = 350;
const getListHeight = (itemsCount: number) => {
  const itemsHeight = itemsCount * ITEM_SIZE + 1; // 1 pixel for correct scroll behavior
  return Math.min(itemsHeight, MAX_LIST_HEIGHT);
};

export interface MultiSelectWithWindowProps<T extends MultiSelectWithWindowItem> {
  items: T[];
  initialCheckedItems?: T[];
  placeholder?: string;
  isLoading?: boolean;
  onCheckedItemsChanged?: (newChecked: T[]) => void;
  isReadOnly?: boolean;
  searchPredicate?: (item: T, term: string) => boolean; // local predicate (ignored in remote mode)
  getDescription?: (item: T) => string;
  filter?: FiltersMap[];
  fetchItems?: (term?: string) => Promise<T[] | void> | void; // load more callback (term optional)
  remoteSearch?: boolean; // if true, delegate searching to parent
  onRemoteSearchTermChange?: (term: string) => void;
  hasMore?: boolean;
  debounceMs?: number;
}

export const MultiSelectWithWindow = <T extends MultiSelectWithWindowItem>(props: MultiSelectWithWindowProps<T>) => {
  const {
    items,
    filter,
    initialCheckedItems,
    searchPredicate,
    getDescription,
    remoteSearch,
    onRemoteSearchTermChange,
    hasMore,
    debounceMs = 250,
  } = props;

  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<Input>(null);
  const listenerId = useRef("");
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [viewItems, setViewItems] = useState<T[]>([]);

  const [checkedItemsMap, setCheckedItemsMap] = useState<ItemsMap<T>>(getCheckedItems(items, initialCheckedItems));

  const { outerRef, onScroll, resetEndGuard } = useEndReached({
    loadMore: props.fetchItems ?? (() => {}),
    threshold: 240,
    horizontal: false,
    hasMore: hasMore ?? true,
  });

  // after you append items:
  useEffect(() => {
    resetEndGuard();
  }, [items.length, resetEndGuard]);

  useEffect(() => {
    return () => globalEvents.unsubscribe(listenerId.current);
  }, []);

  const updateListView = useCallback(
    (allItems: T[], term: string, checkedItems?: ItemsMap<T>) => {
      if (remoteSearch) {
        // In remote mode we don't apply local text filtering; server already filtered.
        const remoteFiltered =
          filter !== undefined
            ? allItems.filter(
                (item: T) =>
                  !filter.some((filterItem) =>
                    typeof filterItem === "string" ? filterItem === item.value : filterItem.value === item.value,
                  ),
              )
            : allItems;
        // Also filter out items that are already checked
        const finalFiltered = checkedItems
          ? remoteFiltered.filter((item: T) => !checkedItems[item.value])
          : remoteFiltered;
        setViewItems(finalFiltered);
        return;
      }
      const filtered = !term ? allItems : allItems.filter((item) => searchPredicate?.(item, term));
      const newFiltered =
        filter !== undefined
          ? filtered.filter(
              (item: T) =>
                !filter.some((filterItem) =>
                  typeof filterItem === "string" ? filterItem === item.value : filterItem.value === item.value,
                ),
            )
          : filtered;

      // Also filter out items that are already checked
      const finalFiltered = checkedItems ? newFiltered.filter((item: T) => !checkedItems[item.value]) : newFiltered;

      setViewItems(finalFiltered);
    },
    [filter, searchPredicate, remoteSearch],
  );

  const initListView = (checkedItems: ItemsMap<T>, term: string) => {
    updateListView(items, term, checkedItems);
  };

  const toggle = (e: SyntheticEvent) => {
    e.stopPropagation();
    if (!isOpen) {
      open();
      inputRef.current?.focus();
    } else {
      close();
    }
  };

  // Keep checked items in sync with external filter & current items while:
  // 1. Removing items no longer present in external filter (supports reset)
  // 2. Preserving selected items that are not in the current items page (e.g. due to search/pagination)
  useEffect(() => {
    if (!filter) return; // nothing to sync
    setCheckedItemsMap((prev) => {
      const next: ItemsMap<T> = getCheckedItems(items, filter as any);
      // For each entry in external filter (array of values or objects), ensure it's represented.
      const flat: any[] = ([] as any[]).concat(filter as any);
      flat.forEach((entry) => {
        const value = typeof entry === "string" || typeof entry === "number" ? entry : entry?.value;
        if (value == null) return;
        if (!next[value] && prev[value]) {
          // Preserve previously selected object if not in current page.
          next[value] = prev[value];
        }
      });
      return next;
    });
  }, [filter, items]);

  // Update the visible list when search term, items, or remote flag changes.
  useEffect(() => {
    updateListView(items, search, checkedItemsMap);
  }, [items, search, remoteSearch, updateListView, checkedItemsMap]);

  const open = () => {
    listenerId.current = globalEvents.subscribeToOutsideClick(listRef.current!, close);
    setIsOpen(true);
    initListView(checkedItemsMap, search);
  };

  const close = () => {
    globalEvents.unsubscribe(listenerId.current);
    setIsOpen(false);
  };

  // Debounce remote search term emission
  const debounceRef = useRef<number | null>(null);
  const searchItem = (_: any, { value }: { value: string }) => {
    setSearch(value);
    if (!isOpen) {
      open();
    }
    if (remoteSearch) {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
      debounceRef.current = window.setTimeout(() => {
        onRemoteSearchTermChange?.(value);
      }, debounceMs);
    } else {
      updateListView(items, value, checkedItemsMap);
    }
  };

  const clearSearchInput = () => {
    // issue with outside click handler
    setTimeout(() => {
      setSearch("");
      if (remoteSearch) {
        onRemoteSearchTermChange?.("");
      }
      initListView(checkedItemsMap, remoteSearch ? search : "");
      inputRef.current?.focus();
    }, 0);
  };

  // Clear search whenever dropdown transitions to closed to avoid stale value showing next open
  useEffect(() => {
    if (!isOpen && search) {
      setSearch("");
      if (remoteSearch) onRemoteSearchTermChange?.("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const isClearable = () => {
    return search.length !== 0;
  };

  const checkItem = (e: SyntheticEvent, checkedItem: T) => {
    e?.stopPropagation();
    clearSearchInput();
    let newItems: ItemsMap<T> = { ...checkedItemsMap };
    if (Object.values(newItems).some((item) => item.value === checkedItem.value)) {
      newItems = Object.fromEntries(
        Object.entries(newItems).filter(([_, item]) => item.value !== checkedItem.value),
      ) as ItemsMap<T>;
      e?.stopPropagation();
      // Fix in a future iteration to not have reportingFilter specific logic here.
    } else if (
      checkedItem.value === "All Customer Accounts" ||
      Object.values(newItems).some((item) => item.value === "All Customer Accounts")
    ) {
      newItems = { [checkedItem.value]: checkedItem } as ItemsMap<T>;
    } else {
      newItems[checkedItem.value] = checkedItem;
    }

    setTimeout(() => {
      setCheckedItemsMap(newItems);
      props.onCheckedItemsChanged?.(Object.values(newItems));
      // keep search text while selecting so user can multi-select; only clear if not remoteSearch
      if (!remoteSearch) {
        setSearch("");
        inputRef.current?.focus();
      }
    }, 0);
  };

  const renderDescription = (item: T) => {
    if (!getDescription) return null;

    const desc = getDescription(item);
    // TextTruncate with lines={2} is not working correct here
    return (
      <TextDotDotDot clamp={2} className="item-description">
        {desc}
      </TextDotDotDot>
    );
  };

  const renderLazyItem = (index: number, style: object) => {
    const item = viewItems[index];
    return (
      <div style={style}>
        {/* These are not rendered if they are selected already */}
        <div
          aria-selected={false}
          role="option"
          className="dropdown-item"
          data-testid="dropdown-item"
          onClick={(e) => checkItem(e, item)}
        >
          {renderDescription(item)}
        </div>
      </div>
    );
  };

  const renderLazyList = () => {
    if (isOpen) {
      return (
        <LazyList
          className="checklist"
          height={getListHeight(viewItems.length)}
          itemCount={viewItems.length}
          itemSize={ITEM_SIZE}
          width={"100%"}
          onScroll={onScroll}
          outerRef={outerRef}
        >
          {({ index, style }) => renderLazyItem(index, style)}
        </LazyList>
      );
    }
  };

  const { isReadOnly, placeholder } = props;

  return (
    <div
      ref={listRef}
      className={cn("checklist-dropdown-v3 ui fluid multiple search selection dropdown dropdown-control", {
        disabled: isReadOnly,
        isOpen,
      })}
      tabIndex={1}
      role="listbox"
      aria-label="Checklist Dropdown"
      aria-expanded={isOpen}
      onClick={toggle}
    >
      {
        // For each selected, draw a label
        Object.values(checkedItemsMap).map((item) => {
          if (item === undefined) {
            return null;
          }
          return (
            <Label key={item.value}>
              {item.text} <Icon name="times" link onClick={(e: SyntheticEvent) => checkItem(e, item)} />
            </Label>
          );
        })
      }
      <Input
        ref={inputRef}
        onChange={searchItem}
        autoFocus
        placeholder={filter !== undefined && filter.length !== 0 ? null : placeholder}
        icon="search"
        iconPosition="left"
        action={{
          icon: isClearable() ? <Icon disabled className="times" /> : undefined,
          onClick: clearSearchInput,
        }}
        value={search}
        fluid
        className={"search"}
      />
      <i aria-hidden="true" className="dropdown icon" />
      {renderLazyList()}
    </div>
  );
};
